define('services/userService',[
    'jquery',
    'moment',
    'lib/lodash4',
    'lib/bluebird',
    'lib/knockout',

    'models/User',

    'services/apiCore',
    'services/authService',
    'services/embeddingService',
    'services/liveDataService',
    'services/loggingService',
    'services/navHelper',
    'services/notificationService'

], function (
    $,
    moment,
    _,
    Promise,
    ko,

    User,

    apiCore,
    authService,
    embeddingService,
    liveDataService,
    LOG,
    navHelper,
    notificationService
) {
    'use strict';

    var currentUser = ko.observable(null);

    /**
     * Service for managing users. Used to get/list/update user data.
     * @exports services/userService
     */
    var userService = {

        /**
         * Observable attribute to track the data for the current user.
         * @returns {object|null} An observable object that contains the data about the
         *                        user that is currently logged in. If the user is not
         *                        logged in, this attribute's value will default to null.
         */
        currentUser: currentUser,

        /**
         * Get a list of all users
         * @returns {object} A promise object to handle the asynchronous response
         *                   from the server. When the response is received, you can
         *                   expect the response data to be an observable  array that
         *                   contains a user object and whose attributes are observable by
         *                   knockout.js.
         */
        getUsers: function (parameters) {
            if (_.has(parameters, "name")) {
                parameters.name = $.trim(parameters.name.replace(/\*/g, ""));

                if (!_.isEmpty(parameters.name)) {
                    if (!_.isEmpty(parameters.divisionId)) {
                        return this.getUsersByNameAndDivisionIds(parameters);
                    }
                    return this.getUsersByName(parameters);
                }
            }

            return apiCore.v2.get('/users', parameters)
              .then(function (response) {
                var arr = _.map(response.entities, function (userData) {
                    return new User(userData);
                });

                return ko.observableArray(arr);
            });
        },

        /**
         * Get a list of all users with metadata from request
         * @returns {object} A promise object to handle the asynchronous response
         *                   from the server. When the response is received, you can
         *                   expect the responses entities property to be an observable array that
         *                   contains a user object and whose attributes are observable by
         *                   knockout.js.
         */
        getUsersAndReturnMetadata: function (parameters) {
            if (_.has(parameters, "name")) {
                parameters.name = $.trim(parameters.name.replace(/\*/g, ""));

                if (!_.isEmpty(parameters.name)) {
                    return this.getUsersByName(parameters);
                }
            }

            return apiCore.v2.get('/users', parameters)
              .then(function (response) {
                var arr = _.map(response.entities, function (userData) {
                    return new User(userData);
                });
                response.entities = ko.observableArray(arr);

                return response;
            });
        },

        getUsersByName: function (parameters) {
            var opts = _.assign({
                pageNumber: 1,
                pageSize: 100
            }, parameters);

            var payload = {
                "sortOrder": opts.sortOrder || "asc",
                "sortBy": "name",
                "pageSize": opts.pageSize,
                "pageNumber": opts.pageNumber,
                "query": [{
                    "type": "CONTAINS",
                    "fields": ["name"],
                    "value": (opts.name || '').toLowerCase()
                }]
            };

            return this.searchUsers(payload);
        },

        getUsersByIdsAndDivisionIds: function (userIds, divisionIds) {
            var opts = {
                pageNumber: 1,
                pageSize: 100
            };

            var payload = {
                "sortOrder": opts.sortOrder || "asc",
                "sortBy": "name",
                "pageSize": opts.pageSize,
                "pageNumber": opts.pageNumber,
                "query": [
                    {
                        "fields": ["id"],
                        "values": (userIds || []),
                        "type": "EXACT",
                        "operator": "AND"
                    }
                ]
            };

            if (divisionIds && divisionIds.length) {
                payload.query.push(
                    {
                        "fields": ["divisionId"],
                        "values": divisionIds,
                        "type": "EXACT",
                        "operator": "AND"
                    }
                );
            }
  
            return this.searchUsers(payload);
        },

        getUsersByNameAndDivisionIds: function (parameters) {
            var opts = _.assign({
                pageNumber: 1,
                pageSize: 100
            }, parameters);

            if (!opts.divisionId) {
                return this.getUsersByName(parameters);
            }

            if (_.isString(opts.divisionId)) {
                opts.divisionId = [opts.divisionId];
            }

            var payload = {
                "sortOrder": opts.sortOrder || "asc",
                "sortBy": "name",
                "pageSize": opts.pageSize,
                "pageNumber": opts.pageNumber,
                "query": [
                    {
                        "type": "CONTAINS",
                        "fields": ["name"],
                        "value": (opts.name || '').toLowerCase()
                    },
                    {
                        "type": "EXACT",
                        "fields": ["divisionId"],
                        "values": opts.divisionId
                    }
                ]
            };

            return this.searchUsers(payload);
        },

        /**
         * Get a specific user by id.
         * @param {string} userId a user id
         * @returns {object} A promise object to handle the asynchronous response
         *                   from the server. When the response is received, you can
         *                   expect the response data to be an object that represents a
         *                   user and whose attributes are observable by knockout.js.
         */
        getUser: function (userId, parameters) {
            return apiCore.v2.get('/users/' + userId, parameters)
                .then(function (response) {
                    return new User(response);
                });
        },
        /**
         * Gets the list of users instances by their Ids.
         * @param {Array} The array of user Ids
         * @returns {promise} A promise that is resolved once we've fetched the items.
         */
        listInstancesById: function(userIds) {
            return apiCore.v2.get('/users', {id : userIds.join(',')});
        },

        /**
         * Update the attributes of a user.
         *
         * @param {string} userId a user id
         * @param {object} data   an object representing the attributes on the user object
         *                        to update.
         * @returns {object} A promise to handle the asynchronous response
         *                   from the server. When the response is received, you can
         *                   expect the response data to be an object that represents a
         *                   user and whose attributes are observable by knockout.js.
         */
        updateUser: function (userId, data) {
            return apiCore.v2.put('/users/' + userId, data)
                .then(function (response) {
                    return new User(response);
                });
        },

        userNotResponding: ko.computed(function () {
            return currentUser() && currentUser().isNotResponding();
        }),

        /**
         * Searches for users that meet the criteria of the query
         *
         * @param {object} query
         *
         * @returns {object} A promise object to handle the asynchronous response
         *                   from the server. When the response is received, you can
         *                   expect the response data to be an observable  array that
         *                   contains a user object and whose attributes are observable by
         *                   knockout.js.
         */
        searchUsers: function (query) {
            // api/v2/users wants ascending/descending but api/v2/users/search requires ASC/DESC
            if (query.sortOrder === 'ascending') {
                query.sortOrder = 'ASC';
            } else if (query.sortOrder === 'descending') {
                query.sortOrder = 'DESC';
            }
            return apiCore.v2.post('/users/search', query)
                .then(function (response) {
                    var arr = _.map(response.results, function (userData) {
                        return new User(userData);
                    });

                    return ko.observableArray(arr);
                });
        },

        /**
         * Searches for users that meet the criteria of the query and returns
         * metadata object
         *
         * @param {object} query
         *
         * @return {Promise} A promise object that will resolve with passing in a response
         *  object sent from the backend. The "results" attribute of this object be an
         *  array that contains a collection of User objects.
         */
        searchUsersAndReturnMetadata: function (query) {
            return apiCore.v2.post('/users/search', query)
                .then(function (response) {
                    response.results = _.map(response.results, function (userData) {
                        return new User(userData);
                    });

                    return response;
                });
        },

        /**
         * Searches for users that meet the criteria of the query and returns
         * metadata object
         *
         * @param {object} query
         *
         * @return {Promise} A promise object that will resolve with passing in a response
         *  object sent from the backend. The "results" attribute of this object be an
         *  array that contains a collection of User objects.
         */
        getDirectReports: function (userId, parameters) {
            return apiCore.v2.get('/users/' + userId + '/directreports', parameters)
                .then(function (response) {
                    return _.map(response, function (userData) {
                        return new User(userData);
                    });
                });
        }
    };

    var updateUserPresence = function (newPresence) {
        User.merge(userService.currentUser(), { presence: newPresence });
        userService.currentUser().presence.valueHasMutated();
        LOG.debug('Got presence update for user', newPresence);
    };

    var updateOutOfOffice = function (newOOOStatus) {
        User.merge(userService.currentUser(), { outOfOffice: newOOOStatus});
        userService.currentUser().outOfOffice.valueHasMutated();
        LOG.debug('Updated OOO status:', newOOOStatus);
    };

    /**
     * Keep the current user object synced with the logged in user.
     */
    ko.computed(function () {
        var userId = authService.principalId();
        if (userId) {
            userService.getUser('me')
                .then(function (user) {
                    userService.currentUser(user);
                });
        } else {
            userService.currentUser(null);
        }
    });

    var presenceTopic, outOfOfficeTopic;

    ko.computed(function () {
        var currentUser = userService.currentUser();

        if (currentUser && notificationService.connected()) {

            presenceTopic = 'v2.users.' + currentUser.id + '.presence';
            outOfOfficeTopic = 'v2.users.' + currentUser.id + '.outofoffice';

            // Empty these out before we request new data
            // subscribe, and fill our user model.
            currentUser.routingStatus({
                status: ko.observable(undefined),
                startTime: ko.observable(undefined)
            });
            currentUser.presence({});
            currentUser.outOfOffice({
                startDate: ko.observable(undefined),
                active: ko.observable(undefined)
            });

            liveDataService
                .getLiveData('v2.users.' + currentUser.id + '.routingStatus', userService.currentUser)
                .catch(function () {
                    LOG.error('Unable to subscribe to routingStatus');
                })
                .then(function () {
                    return apiCore.v2.get('/users/' + currentUser.id + '/routingStatus');
                })
                .then(function (routingStatus) {
                    if (!currentUser.routingStatus().status()) {
                        User.fill(currentUser, {routingStatus: routingStatus});
                    }
                });

            notificationService.subscribe(presenceTopic, updateUserPresence)
                .catch(function () {
                    LOG.error('Unable to subscribe to presence');
                })
                .then(function () {
                    LOG.debug('Subscribed to carrier pidgeon presence notifications for current user with id: ' + currentUser.id);
                    return apiCore.v2.get('/users/me?expand=presence');
                })
                .then(function (userData) {
                    if (!currentUser.presence().presenceDefinition && userData.presence) {
                        User.fill(currentUser, {
                            presence: _.omit(userData.presence, "user")
                        });
                        currentUser.presence.valueHasMutated();
                    }
                });

            notificationService.subscribe(outOfOfficeTopic, updateOutOfOffice)
                .catch(function () {
                    LOG.error('Unable to subscribe to outofoffice');
                })
                .then(function () {
                    return apiCore.v2.get('/users/' + currentUser.id + '/outofoffice');
                })
                .then(function (oooStatus) {
                    if (!currentUser.outOfOffice().active()) {
                        User.fill(currentUser, {
                            outOfOffice: _.omit(oooStatus, "user")
                        });
                        currentUser.outOfOffice.valueHasMutated();
                    }
                });

        } else {
            liveDataService.cancelLiveData(userService.currentUser);

            if (presenceTopic) {
                notificationService.unsubscribe(presenceTopic, updateUserPresence);
            }

            if (outOfOfficeTopic) {
                notificationService.unsubscribe(outOfOfficeTopic, updateOutOfOffice);
            }
        }
    });

    return userService;
});

